import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { DateTime } from 'luxon';

import Layout from '../../layout/Layout';
import Card from '../../components/Card/Card';
import Table from '../../components/Table/Table';
import TableHead from '../../components/Table/TableHead';
import TableHeadData from '../../components/Table/TableHeadData';
import TableRow from '../../components/Table/TableRow';
import TableData from '../../components/Table/TableData';
import Badge from '../../components/Badge/Badge';
import ButtonTypes from '../../constants/buttonTypes';
import TableBody from '../../components/Table/TableBody';
import EmptyTable from '../../components/Table/EmptyTable';
import getLogs from '../../thunks/logs/getLogs';
import UserCardSmall from '../../components/UserCardSmall/UserCardSmall';
import LogData from './components/LogData';
import ActionType from './components/ActionType';
import Pagination from '../../components/Pagination/Pagination';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../common/permissionGroup';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import UserLogCardHeader from './components/UserLogCardHeader';
import LogDetails from './components/LogDetails';
import { resetLogsFilter, setLogsFilter } from '../../actions/logs/logsFilter';
import SearchInput from '../../components/SearchInput/SearchInput';
import useQueryParams from '../../hooks/useQueryParams';

const Logs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedLog, setSelectedLog] = useState();
  const [queryParams, setQueryParams] = useQueryParams();
  const {
    userLogs: {
      loading, data, currentPage, lastPage, error
    },
    logsFilter: {
      query
    }
  } = useSelector((state) => ({
    userLogs: state?.getLogs,
    logsFilter: state?.logsFilter
  }), shallowEqual);

  useEffect(() => {
    return () => {
      dispatch(resetLogsFilter());
    };
  }, []);

  const breadcrumbs = {
    pageTitle: 'Log Overview',
    history: [
      {
        title: 'Home',
        onClick: () => navigate('/')
      },
      {
        title: 'Logs',
      },
    ]
  };

  const onPagePress = (page) => {
    batch(() => {
      dispatch(setLogsFilter({ page }));
      dispatch(getLogs());
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => batch(() => {
      dispatch(setLogsFilter({ page: 1 }));
      dispatch(getLogs());
    }), 800);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    dispatch(setLogsFilter({ page: 1, query: queryParams.query || '' }));
  }, [queryParams.query]);

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <PermissionsGate
        can={permissions.LOGS_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          showHeader
          headerRight={<UserLogCardHeader onReset={() => setQueryParams({})} />}
          headerLeft={(
            <div className="flex w-[250px]">
              <SearchInput
                isClearable
                value={query}
                onChange={({ target: { value } }) => {
                  dispatch(setLogsFilter({ query: value }));
                  setQueryParams(value ? { query: value } : {});
                }}
              />
            </div>
          )}
          headerBordered={false}
          footer={(
            <div className="flex justify-end">
              <Pagination
                currentPage={currentPage}
                onPagePress={onPagePress}
                lastPage={lastPage}
              />
            </div>
          )}
          footerBordered={false}
        >
          <div>
            {error && (
              <div role="alert" className="text-danger p-[12px]">
                {`Could not load logs: ${error}`}
              </div>
            )}
            <Table loading={loading}>
              <TableHead>
                <TableRow bordered>
                  <TableHeadData>Local time</TableHeadData>
                  <TableHeadData>
                    user
                  </TableHeadData>
                  <TableHeadData>
                    action
                  </TableHeadData>
                  <TableHeadData>
                    type
                  </TableHeadData>
                  <TableHeadData>Event</TableHeadData>
                  <TableHeadData>Details</TableHeadData>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((log, index) => {
                  const {
                    action, created_at: date, user, type, data: logData, id
                  } = log;
                  const name = type?.name || 'Unknown';
                  return (
                    <TableRow key={id} bordered={index < data.length - 1}>
                      <TableData>
                        <Badge
                          text={DateTime.fromISO(date, { zone: 'utc' }).toLocal()
                          .toFormat('yyyy-MM-dd HH:mm:ss ZZZZ')}
                          type={ButtonTypes.LIGHT}
                        />
                      </TableData>
                      <TableData>
                        <UserCardSmall user={user} />
                      </TableData>
                      <TableData>
                        <ActionType type={action} />
                      </TableData>
                      <TableData>
                        <Badge text={name} type={ButtonTypes.LIGHT} />
                      </TableData>
                      <TableData>
                        <div className="flex justify-start">
                          <LogData logData={logData} type={name} action={action} />
                        </div>
                      </TableData>
                      <TableData>
                        <button
                          type="button"
                          className="px-[9px] py-[6px] rounded bg-primary-light text-primary"
                          onClick={() => setSelectedLog(log)}
                        >
                          View
                        </button>
                      </TableData>
                    </TableRow>
                  );
                })}
                {!loading && !data?.length && <EmptyTable colSpan={6} text="No logs found" />}
              </TableBody>
            </Table>
            <LogDetails log={selectedLog} onClose={() => setSelectedLog(undefined)} />
          </div>
        </Card>
      </PermissionsGate>
    </Layout>
  );
};
export default Logs;
